import React, { type ReactNode } from 'react';
import type { BarDuration } from '@einhaus/project314-types';
import { STANDARD_DURATIONS } from '@einhaus/project314-shared';

export interface OverlayToggle {
    key: string;
    label: string;
    active: boolean;
    onToggle: (value: boolean) => void;
    visible?: boolean;
}

interface ChartToolbarProps {
    actions?: ReactNode;
    className?: string;
    duration: BarDuration;
    overlayToggles: OverlayToggle[];
    onDurationChange: (duration: BarDuration) => void;
}

const TogglePill = ({ active, label, onClick }: { active: boolean; label: string; onClick: () => void }) => (
    <button
        className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all whitespace-nowrap ${
            active
                ? 'bg-blue-500/20 text-blue-300 border border-blue-500/40 shadow-[0_0_6px_rgba(59,130,246,0.15)]'
                : 'bg-zinc-800/80 text-zinc-500 border border-zinc-700/50 hover:text-zinc-300 hover:border-zinc-600'
        }`}
        type="button"
        onClick={onClick}
    >
        {label}
    </button>
);

const ChartToolbar: React.FC<ChartToolbarProps> = ({ actions, className = '', duration, overlayToggles, onDurationChange }) => {
    const visibleToggles = overlayToggles.filter((t) => t.visible !== false);

    return (
        <div className={`flex flex-wrap items-center gap-2 py-2 ${className}`}>
            {/* Duration selector */}
            <div className="inline-flex rounded-md shadow-sm">
                {STANDARD_DURATIONS.map((durationOption, index) => (
                    <button
                        className={`
                            px-2.5 py-1 text-xs font-medium transition-colors border
                            ${index === 0 ? 'rounded-l-md' : ''}
                            ${index === STANDARD_DURATIONS.length - 1 ? 'rounded-r-md' : ''}
                            ${index > 0 ? '-ml-px' : ''}
                            ${
                                duration === durationOption
                                    ? 'bg-blue-600 text-white border-blue-500 z-10 relative'
                                    : 'bg-zinc-800 text-zinc-400 border-zinc-700 hover:text-zinc-200 hover:bg-zinc-700'
                            }
                        `}
                        key={durationOption}
                        type="button"
                        onClick={() => onDurationChange(durationOption)}
                    >
                        {durationOption}
                    </button>
                ))}
            </div>

            {/* Divider */}
            {visibleToggles.length > 0 && <div className="w-px h-5 bg-zinc-700 mx-1 hidden md:block" />}

            {/* Overlay toggle pills */}
            <div className="flex flex-wrap items-center gap-1.5">
                {visibleToggles.map((toggle) => (
                    <TogglePill
                        active={toggle.active}
                        key={toggle.key}
                        label={toggle.label}
                        onClick={() => toggle.onToggle(!toggle.active)}
                    />
                ))}
            </div>

            {/* Right-aligned actions */}
            {actions && <div className="ml-auto flex items-center gap-2">{actions}</div>}
        </div>
    );
};

export default ChartToolbar;
